import { useMemo, useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import FaqSection from "../components/FaqSection.js";
import BlogCard from "../components/BlogCard";

import { usePublicBlogsListQuery } from "../store/generated.ts";
import { useNavigate } from "react-router-dom";
import { formatBlogDate } from "../utils/formatDate.js";
import { ReactComponent as EmptyState } from "./../assets/svgs/empty-state.svg";
import { landingDesktopItems, landingMobileItems } from "../data/mockData.js";
import Footer from "../components/Footer.js";

const EvenessBlog = () => {
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [allResults, setAllResults] = useState([]);
  const navigate = useNavigate();

  const { data, isLoading, isFetching, isError } = usePublicBlogsListQuery({
    page,
    pageSize,
  });

  useEffect(() => {
    setPage(1);
    setAllResults([]);
  }, [pageSize]);

  useEffect(() => {
    const incoming = data?.results ?? [];
    if (!incoming.length) return;

    setAllResults((prev) => {
      const seen = new Set(prev.map((x) => x?.id));
      const merged = [...prev];

      for (const item of incoming) {
        if (item?.id == null) continue;
        if (!seen.has(item.id)) {
          merged.push(item);
          seen.add(item.id);
        }
      }
      return merged;
    });
  }, [data?.results]);

  const blogs = useMemo(() => {
    return (allResults ?? []).map((b, idx) => ({
      id: b?.id,
      slug: b?.slug ?? "",
      title: b?.title ?? "",
      content: b?.preview ?? b?.content_md ?? "",
      author: b?.author_name ?? "",
      date: formatBlogDate(b?.published_at),
      imageSrc: b?.hero_image_url || "",
      href: b?.slug ? `/blog/${b.slug}` : "#",
    }));
  }, [allResults]);

  const canViewMore = Boolean(data?.next);

  const showEmptyState =
    !isLoading && !isFetching && !isError && (blogs?.length ?? 0) === 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <Navbar desktopItems={landingDesktopItems} mobileItems={landingMobileItems} />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-10 mb-16">
        {/* Empty State */}
        {showEmptyState && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <EmptyState />
            <h3 className="text-2xl sm:text-3xl font-semibold text-[#22272A] font-['Poppins']">
              No blogs found!
            </h3>
          </div>
        )}

        {/* Grid */}
        {!showEmptyState && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(isLoading && page === 1 ? Array.from({ length: 6 }) : blogs).map(
              (j, i) => (
                <div key={j?.id ?? i} className="h-full">
                  {isLoading && page === 1 ? (
                    <div className="h-[360px] rounded-[16px] bg-[#EEF2F5]" />
                  ) : (
                    <BlogCard
                      title={j.title}
                      content={j.content}
                      author={j.author}
                      date={j.date}
                      imageSrc={j.imageSrc}
                      href={j.href}
                    />
                  )}
                </div>
              ),
            )}
          </div>
        )}

        {/* Errors */}
        {isError && (
          <div className="text-center mt-6 text-sm text-red-500 font-['Poppins']">
            Failed to load blogs. Please try again.
          </div>
        )}

        {/* View More */}
        {canViewMore ? (
          <div className="text-center pt-[30px]">
            <button
              onClick={() => {
                if (isFetching) return;
                setPage((p) => p + 1);
              }}
              className="bg-[#22272A] text-[#FFFFFF] px-3 sm:px-6 py-2.5 rounded-lg hover:opacity-90 transition text-base whitespace-nowrap flex-shrink-0"
            >
              <span className="font-['Poppins'] font-normal text-[16px]">
                {isFetching ? "Loading..." : "View More"}
              </span>
            </button>
          </div>
        ) : null}
      </section>
      <FaqSection />
      <Footer/>
    </div>
  );
};

export default EvenessBlog;
